// apps/client/src/api/team.ts
import {
  UserWithRole,
  UserRole,
  ApiResponseGeneric,
  OrganisationTeamResponse,
  OrganisationTeamUserAddResponse,
  OrganisationTeamUserDeleteResponse,
} from '@avanzu/types';
import { apiClient } from './apiClient';

// Helper function for consistent response handling
const handleResponse = async (response: Response): Promise<ApiResponseGeneric> => {
  const result: ApiResponseGeneric = await response.json();

  if (!response.ok || !result.success) {
    throw new Error(result.message || `Request failed: ${response.statusText}`);
  }

  return result;
};

export const fetchTeamMembers = async (organisationId: string): Promise<UserWithRole[]> => {
  const response = await apiClient.get(`/organisations/${organisationId}/team`);

  if (!response.ok) {
    throw new Error(`Failed to fetch team members: ${response.statusText}`);
  }

  const result: OrganisationTeamResponse = await response.json();
  return result.users || [];
};

export const updateMemberRole = async (
  organisationId: string,
  memberId: string,
  role: UserRole
): Promise<UserWithRole> => {
  const response = await apiClient.put(`/organisations/${organisationId}/team/${memberId}`, {
    role,
  });

  // Server returns the OrganisationTeamUserAddResponse shape directly
  // ({ success, message, user }) — not wrapped in `{ data: ... }`.
  const apiResponse = (await response.json()) as OrganisationTeamUserAddResponse;

  if (!response.ok || !apiResponse.success) {
    throw new Error(apiResponse.message || `Failed to update member role: ${response.statusText}`);
  }

  return apiResponse.user;
};

export const deleteMember = async (
  organisationId: string,
  memberId: string
): Promise<{ userId: string }> => {
  const response = await apiClient.delete(`/organisations/${organisationId}/team/${memberId}`);

  if (!response.ok) {
    throw new Error(`Failed to delete team members: ${response.statusText}`);
  }

  const result: OrganisationTeamUserDeleteResponse = await response.json();
  return { userId: result.userId };
};

export const leaveOrganisation = async (organisationId: string): Promise<ApiResponseGeneric> => {
  const response = await apiClient.delete(`/user/organisations/${organisationId}`);
  return await handleResponse(response);
};
